import { faker } from '@faker-js/faker'
import { add, formatISO } from 'date-fns'

import {
  generatePayment,
  type Payment,
  PaymentMethod,
  PaymentProcessor,
  validatePayment,
} from './payment'
import { PRODUCT_ITEMS } from './products'
import { type MonthlyDistribution, sampleNormal } from './utils'
import {
  allocateRandomChunks,
  getRandomBoolean,
  isObject,
  oneDayMonthlyPast12Months,
  past12MonthsFridays,
  randomIntegerInRange,
} from './utils'

export type InvoiceLineItem = {
  description: string
  product: string
  unit_price: number
  quantity: number
}

export type InvoiceTag = {
  key: string
  value: string
}

export type Invoice = {
  external_id?: string
  invoice_number?: string
  sent_at: string
  due_at: string
  additional_discount?: number
  line_items: InvoiceLineItem[]
  payments: Payment[]
  tags?: InvoiceTag[]
}

const WEEKLY_AVERAGE_PAYOUT = 10000_00

export const validateInvoiceLineItem = (
  item: unknown,
): item is InvoiceLineItem => {
  return (
    isObject(item)
    && 'description' in item
    && typeof item.description === 'string'
    && 'product' in item
    && typeof item.product === 'string'
    && 'unit_price' in item
    && typeof item.unit_price === 'number'
    && 'quantity' in item
    && typeof item.quantity === 'number'
  )
}

export const validateInvoices = (data: unknown): data is Invoice[] => {
  return (
    Array.isArray(data)
    && data.every((invoice: unknown) => {
      return (
        isObject(invoice)
        && 'sent_at' in invoice
        && typeof invoice.sent_at === 'string'
        && 'due_at' in invoice
        && typeof invoice.due_at === 'string'
        && 'line_items' in invoice
        && Array.isArray(invoice.line_items)
        && invoice.line_items.every(validateInvoiceLineItem)
        && 'payments' in invoice
        && Array.isArray(invoice.payments)
        && invoice.payments.every(validatePayment)
      )
    })
  )
}

export const generateInvoiceLineItem = (
  overrides?: Partial<InvoiceLineItem>,
): InvoiceLineItem => {
  const quantity = randomIntegerInRange(1, 10)
  const unit_price = randomIntegerInRange(1000, 100000)
  const { name, description } = faker.helpers.arrayElement(PRODUCT_ITEMS)

  return {
    product: name,
    description: description,
    unit_price,
    quantity,
    ...overrides,
  }
}

export const generateInvoiceForPayout = (
  roughAmount?: number,
  overrides?: Partial<Invoice>,
): Invoice => {
  const externalId = overrides?.external_id || faker.string.uuid()
  const roughInvoiceAmount =
    roughAmount
    ?? Math.round((WEEKLY_AVERAGE_PAYOUT * randomIntegerInRange(80, 120)) / 100)

  const invoiceNumber =
    overrides?.invoice_number
    || `INV-${faker.string.alphanumeric({ length: 5, casing: 'upper' })}`

  const defaultSentAt = faker.date.recent({ days: 365 })
  const sentAt = overrides?.sent_at || formatISO(defaultSentAt)

  const defaultDueAt = add(defaultSentAt, { days: randomIntegerInRange(1, 90) })
  const dueAt = overrides?.due_at || formatISO(defaultDueAt)

  const numLineItems = randomIntegerInRange(1, 10)
  const lineItemAmounts = allocateRandomChunks(roughInvoiceAmount, numLineItems)

  const lineItems =
    overrides?.line_items
    ?? Array.from({ length: numLineItems }, (_, idx) => {
      const quantity = randomIntegerInRange(1, 10)
      const unitPrice = Math.round(lineItemAmounts[idx] / quantity)

      return generateInvoiceLineItem({
        unit_price: unitPrice,
        quantity,
      })
    })

  const subtotal = lineItems.reduce(
    (acc, item) => acc + item.unit_price * item.quantity,
    0,
  )

  const hasDiscount = getRandomBoolean(0.3) // 30% chance of discount
  const discount = hasDiscount
    ? Math.round(subtotal * (randomIntegerInRange(5, 15) / 100)) // 5-15% discount
    : 0
  const additionalDiscount = overrides?.additional_discount || discount

  const fee = Math.round(subtotal * (5 / 100)) // 5% fee
  const totalAmount = subtotal - discount

  let payments: Payment[] | undefined = overrides?.payments
  if (payments === undefined) {
    // Always paid for payouts
    payments = [
      generatePayment({
        external_id: `payment-${externalId}`,
        amount: totalAmount,
        method: PaymentMethod.OTHER,
        processor: PaymentProcessor.PROCESSOR,
        fee: fee,
      }),
    ]
  }

  return {
    external_id: externalId,
    invoice_number: invoiceNumber,
    sent_at: sentAt,
    due_at: dueAt,
    line_items: lineItems,
    additional_discount: additionalDiscount,
    payments,
    ...overrides,
  }
}

export const generateInvoice = (overrides?: Partial<Invoice>): Invoice => {
  const externalId = overrides?.external_id || faker.string.uuid()
  const roughInvoiceAmount = Math.round(
    (WEEKLY_AVERAGE_PAYOUT * randomIntegerInRange(80, 120)) / 100,
  )

  const invoiceNumber =
    overrides?.invoice_number
    || `INV-${faker.string.alphanumeric({ length: 5, casing: 'upper' })}`

  const defaultSentAt = faker.date.recent({ days: 365 })
  const sentAt = overrides?.sent_at || formatISO(defaultSentAt)

  const defaultDueAt = add(defaultSentAt, { days: randomIntegerInRange(1, 90) })
  const dueAt = overrides?.due_at || formatISO(defaultDueAt)

  const numLineItems = randomIntegerInRange(1, 10)
  const lineItemAmounts = allocateRandomChunks(roughInvoiceAmount, numLineItems)

  const lineItems =
    overrides?.line_items
    ?? Array.from({ length: numLineItems }, (_, idx) => {
      const quantity = randomIntegerInRange(1, 10)
      const unitPrice = Math.round(lineItemAmounts[idx] / quantity)

      return generateInvoiceLineItem({
        unit_price: unitPrice,
        quantity,
      })
    })

  const subtotal = lineItems.reduce(
    (acc, item) => acc + item.unit_price * item.quantity,
    0,
  )

  const hasDiscount = getRandomBoolean(0.3) // 30% chance of discount
  const discount = hasDiscount
    ? Math.round(subtotal * (randomIntegerInRange(5, 15) / 100)) // 5-15% discount
    : 0
  const additionalDiscount = overrides?.additional_discount || discount

  const totalAmount = subtotal - discount

  let payments: Payment[] | undefined = overrides?.payments
  if (payments === undefined) {
    if (getRandomBoolean(0.5)) {
      payments = [] // unpaid
    } else {
      const isPartial = getRandomBoolean(0.5)
      const amount = isPartial
        ? Math.round(totalAmount * (randomIntegerInRange(30, 70) / 100)) // 30%-70% paid
        : totalAmount

      payments = [
        generatePayment({
          external_id: `payment-${externalId}`,
          amount,
          method: PaymentMethod.ACH,
        }),
      ]
    }
  }

  return {
    external_id: externalId,
    invoice_number: invoiceNumber,
    sent_at: sentAt,
    due_at: dueAt,
    line_items: lineItems,
    additional_discount: additionalDiscount,
    payments,
    ...overrides,
  }
}

export const getInvoices = (demoName: string): Invoice[] => {
  const pastFridays = past12MonthsFridays()
  return pastFridays.map((transactionDate, idx) =>
    generateInvoice({
      external_id: `${demoName}-invoice-${idx}`,
      sent_at: formatISO(transactionDate),
      due_at: formatISO(
        add(transactionDate, { days: faker.number.int({ min: 1, max: 90 }) }),
      ),
    }),
  )
}

export const getInvoicesForPayouts = (
  demoName: string,
  dist?: MonthlyDistribution,
): Invoice[] => {
  const monthlyDates = oneDayMonthlyPast12Months(5) // 3rd Friday

  return monthlyDates.map((transactionDate, idx) => {
    const roughAmount = dist
      ? sampleNormal(dist.mean, dist.stdDev, dist.min, dist.max) * 100
      : undefined
    return generateInvoiceForPayout(roughAmount, {
      external_id: `${demoName}-payout-invoice-${idx}`,
      sent_at: formatISO(transactionDate),
      due_at: formatISO(
        add(transactionDate, { days: faker.number.int({ min: 1, max: 90 }) }),
      ),
    })
  })
}
